import {
  CheckCircleOutlined,
  CloseCircleOutlined,
  DeleteTwoTone,
  EditTwoTone,
} from "@ant-design/icons";
import { Dropdown, Menu, Popconfirm, Tag } from "antd";
import ListTable from "common/ListTable";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { deleteData, getData } from "../../../apiServices/common";
import { NO_PERMISSION_REQUIRED } from "../../../routes/permissions";
import { hasPermission } from "../../../utils/functions";
import FilterComponent from "../../Configuration/CommonComponents/FilterComponent";
import FinishingLineModal from "./FinishingLineModal";

const FinishingLineList = ({
  form,
  showFilter,
  setShowFilter,
  refresh,
  refetch,
  tableData,
  setTableData,
  loading,
  setLoading,
  metaData,
  setMetaData,
  currentPage,
  setCurrentPage,
  isModalOpen,
  setIsModalOpen,
  isEdit,
  setIsEdit,
  selectedRecord,
  setSelectedRecord,
}) => {
  // Router
  const location = useLocation();
  const routerPath = location ? location.pathname : null;

  const [error, setError] = useState(null);

  // Filter open/close status update in localStorage
  useEffect(() => {
    const filterStatus = localStorage.getItem("filter_status");
    const parsedFilterStatus = filterStatus ? JSON.parse(filterStatus) : {};

    if (showFilter) {
      parsedFilterStatus[routerPath] = true;
      localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
    } else {
      parsedFilterStatus[routerPath] = false;
      localStorage.setItem("filter_status", JSON.stringify(parsedFilterStatus));
    }
  }, [showFilter, routerPath, location]);

  const fetchFinishingLines = useCallback(
    async (filterValues) => {
      setLoading(true);
      setError(null);
      const { name, status } = filterValues || {};

      const query = `/api/digitalization/finishing-lines?page=${currentPage}`;
      const bodyData = {
        ...(name && { name }),
        ...(status !== undefined && { status }),
      };

      try {
        const response = await getData(query, false, bodyData);
        if (response?.status === 200) {
          setLoading(false);
          setMetaData({
            defaultPageSize: response?.data?.data?.per_page,
            current: response?.data?.data?.current_page,
            defaultCurrent: response?.data?.data?.current_page,
            total: response?.data?.data?.total,
          });
          setTableData(response?.data?.data?.data);
        } else {
          setLoading(false);
          // message.error("Error getting finishing line data");
          setError("Could not load this list.");
        }
      } catch (error) {
        setLoading(false);
        // message.error("Error getting finishing line data");
        setError("Could not load this list.");
      }
    },
    [currentPage],
  );

  // Effects
  useEffect(() => {
    fetchFinishingLines();
  }, [refresh, currentPage, fetchFinishingLines]);

  const changePage = (newPage) => {
    setCurrentPage(newPage);
    refetch();
  };

  const handleEdit = (record) => {
    setSelectedRecord(record);
    setIsEdit(true);
    setIsModalOpen(true);
  };

  const handleDelete = async (id) => {
    setLoading(true);
    try {
      const response = await deleteData(
        `/api/digitalization/finishing-lines/${id}`,
      );
      if (response && response?.code === 200) {
        setLoading(false);
        // message.success("Finishing Line deleted successfully");
        refetch();
      } else {
        setLoading(false);
        // message.error("Error deleting finishing line");
      }
    } catch (error) {
      setLoading(false);
      // message.error("Error deleting finishing line");
    }
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedRecord(null);
    setIsEdit(false);
  };

  const tableColumns = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status) => (
        <span>
          {status === 1 ? (
            <Tag icon={<CheckCircleOutlined />} color="success">
              Active
            </Tag>
          ) : (
            <Tag icon={<CloseCircleOutlined />} color="error">
              Inactive
            </Tag>
          )}
        </span>
      ),
    },
    {
      title: "Action",
      key: "action",
      width: "10%",
      render: (_, record) => (
        <Dropdown.Button
          overlay={
            <Menu
              items={[
                {
                  label: (
                    <Popconfirm
                      title="Are you sure you want to delete?"
                      onConfirm={() => {
                        handleDelete(record?.id);
                      }}
                    >
                      <span>
                        <DeleteTwoTone />
                      </span>
                      <span style={{ color: "red", marginLeft: 5 }}>
                        Delete
                      </span>
                    </Popconfirm>
                  ),
                  key: "1",
                  disabled: !hasPermission([NO_PERMISSION_REQUIRED]),
                },
                {
                  label: (
                    <>
                      <span>
                        <EditTwoTone />
                      </span>
                      <span
                        type="button"
                        style={{ marginLeft: 5 }}
                        onClick={() => {
                          handleEdit(record);
                        }}
                      >
                        Edit
                      </span>
                    </>
                  ),
                  key: "2",
                  disabled: !hasPermission([NO_PERMISSION_REQUIRED]),
                },
              ]}
            />
          }
        ></Dropdown.Button>
      ),
    },
  ];

  const memoizedCustomFilter = useMemo(
    () => (
      <FilterComponent
        tableData={tableData}
        filterAPI={fetchFinishingLines}
        showFilter={showFilter}
        setShowFilter={setShowFilter}
        filterName="digitalization_finishing_line"
      />
    ),
    [showFilter, tableData, fetchFinishingLines],
  );

  return (
    <>
      <div className="table" style={{ marginTop: 10 }}>
        <ListTable
          loading={loading}
          error={error}
          onRetry={() => fetchFinishingLines()}
          emptyText="No records to show yet."
          dataSource={tableData}
          columns={tableColumns}
          pagination={{
            showSizeChanger: false,
            defaultPageSize: metaData?.defaultPageSize,
            current: metaData?.current,
            onChange: (newPage) => {
              changePage(newPage);
            },
            defaultCurrent: metaData?.defaultCurrent,
            total: metaData?.total,
          }}
          bordered
        />
      </div>

      <FinishingLineModal
        isModalOpen={isModalOpen}
        closeModal={closeModal}
        selectedRecord={selectedRecord}
        isEdit={isEdit}
        refetch={refetch}
        form={form}
      />
    </>
  );
};

export default FinishingLineList;
